/**
 * entityContextResolver.js — Live entity enrichment loader
 *
 * Loads real-time pastoral data for the currently active entity and
 * returns a compact context fragment merged into the panorama string.
 *
 * Cache: in-memory Map keyed by "type:id", TTL 3 minutes.
 * Invalidation: call invalidateEntityCache() on MUTATION_SUCCESS events.
 *
 * INVARIANT: all queries go through executeTool — never direct Supabase.
 */

import { executeTool }       from "../../kevia-data/executeTool";
import { compactDataContext } from "../../kevia-data/compactDataContext";

const ENTITY_TTL_MS = 3 * 60 * 1000; // 3 min
const MAX_FRAGMENT  = 200;

const _cache = new Map();

function _cacheKey(entityContext) {
  return `${entityContext?.type ?? "_"}:${entityContext?.id ?? "_"}`;
}

/* ─── Enrichment loaders per entity type ─────────────────────── */

async function enrichFamily(entityContext, appScope) {
  const id = entityContext.id;
  const [overviewResult, timelineResult] = await Promise.allSettled([
    executeTool({ toolId: "get_family_overview",  filters: { entity_id: id }, appScope }),
    executeTool({ toolId: "get_visit_timeline",   filters: { entity_id: id }, appScope }),
  ]);

  const results = [overviewResult, timelineResult]
    .filter((r) => r.status === "fulfilled")
    .map((r) => r.value)
    .filter((r) => r.ok);

  return results;
}

async function enrichPerson(entityContext, appScope) {
  const id = entityContext.id;
  const result = await executeTool({
    toolId:  "get_visit_timeline",
    filters: { entity_id: id },
    appScope,
  });
  return result.ok ? [result] : [];
}

async function enrichChurch(entityContext, appScope) {
  const result = await executeTool({
    toolId:  "get_church_activity_summary",
    filters: {},
    appScope,
  });
  return result.ok ? [result] : [];
}

/* ─── Format fragment ─────────────────────────────────────────── */

function buildFragment(entityContext, results) {
  if (!results.length) return null;
  const body = compactDataContext(results);
  if (!body) return null;

  // Strip the [data: prefix since we'll wrap it in [entidad: ...]
  const inner = body.replace(/^\[data:\s*/, "").replace(/\]$/, "").trim();
  if (!inner) return null;

  const name = entityContext.name ? ` ${entityContext.name} — ` : " ";
  const raw  = `[entidad:${name}${inner}]`;
  return raw.length > MAX_FRAGMENT ? raw.slice(0, MAX_FRAGMENT - 1) + "]" : raw;
}

/* ─── Public API ─────────────────────────────────────────────── */

/**
 * Load live enrichment for the active entity and return a compact fragment.
 * Returns null for entity types with no enrichment adapter (event, visit, prayer, preaching).
 *
 * @param {{ type: string, id: string, name?: string }} entityContext
 * @param {object} appScope — from useKeviaContext().appScope
 * @returns {Promise<string|null>}
 */
export async function resolveEntityLiveContext(entityContext, appScope) {
  if (!entityContext?.type || !entityContext?.id) return null;

  const key    = _cacheKey(entityContext);
  const cached = _cache.get(key);
  if (cached && Date.now() - cached.ts < ENTITY_TTL_MS) return cached.fragment;

  let results = [];
  try {
    if (entityContext.type === "family")  results = await enrichFamily(entityContext, appScope);
    else if (entityContext.type === "person")  results = await enrichPerson(entityContext, appScope);
    else if (entityContext.type === "church")  results = await enrichChurch(entityContext, appScope);
    // event, visit, prayer, preaching → no enrichment (contextBlock already has it)
  } catch {
    // Non-fatal — cache null so we don't retry immediately
  }

  const fragment = buildFragment(entityContext, results);
  _cache.set(key, { fragment, ts: Date.now() });
  return fragment;
}

/**
 * Invalidate the cached enrichment for an entity.
 * Call after a successful mutation that affects the entity.
 *
 * @param {{ type: string, id: string }} entityContext
 */
export function invalidateEntityCache(entityContext) {
  if (!entityContext) return;
  _cache.delete(_cacheKey(entityContext));
}
